import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  startTransition,
} from "react";
import {
  createPlaybackClockAnchor,
  measurePlaybackClockDrift,
  resolveFrameBudgetRemainingMs,
  resolvePlaybackFrameBudgetMs,
  type PlaybackClockDriftSample,
} from "../utils/playbackTiming";
import { resolveEEGViewerPlaybackSchedulerIntervalMs } from "../components/eegViewerPlaybackHelpers";

interface UsePlaybackClockOptions {
  isPlaying: boolean;
  speed: number;
  minTime?: number;
  maxTime?: number;
  initialTime?: number;
  uiFps?: number;
  maxTickDeltaMs?: number;
  /** Page timebase (sec/page); used to keep timer ticks blit-safe at high speed. */
  timeScaleSeconds?: number;
  onTick?: (timeSeconds: number, deltaMs: number) => void;
  onUiPublishRequested?: (event: PlaybackClockUiPublishRequest) => void;
  onSchedulerSample?: (event: PlaybackClockSchedulerSample) => void;
  /** Sprint 1: per-tick timing (drift, frame budget) for shared clock model. */
  onTimingSample?: (event: PlaybackClockTimingSample) => void;
  onEnd?: (timeSeconds: number) => void;
}

export interface PlaybackClockUiPublishRequest {
  requestedAtMs: number;
  playheadTimeSeconds: number;
  deltaMs: number;
  uiPublishIntervalMs: number;
}

export interface PlaybackClockTimingSample {
  scheduler: "timer" | "raf";
  playheadTimeSeconds: number;
  deltaMs: number;
  tickWorkMs: number;
  frameBudgetMs: number;
  frameBudgetRemainingMs: number;
  drift: PlaybackClockDriftSample;
  speed: number;
}

export interface PlaybackClockSchedulerSample {
  scheduler: "timer" | "raf";
  sampleKind: "deadline-miss" | "companion-probe";
  intervalMs: number;
  scheduledAtMs: number;
  targetAtMs: number;
  firedAtMs: number;
  overrunMs: number;
  scheduledLateMs: number;
  callbackLateMs: number;
  missPhase:
    | "none"
    | "before-callback"
    | "after-previous-work"
    | "mixed";
  playheadTimeSeconds: number | null;
  previousTickStartedAtMs: number | null;
  previousTickCompletedAtMs: number | null;
  previousTickWorkMs: number | null;
  callbackCompletedAtMs: number;
  rafDelayMs: number | null;
  messageChannelDelayMs: number | null;
  zeroDelayTimerMs: number | null;
}

const ULTRA_HIGH_SPEED_DEFERRED_STALL_MIN_MISS_MS = 64;
const ULTRA_HIGH_SPEED_DEFERRED_STALL_INTERVAL_MS = 1000;
const ULTRA_HIGH_SPEED_COMPANION_PROBE_MIN_MISS_MS = 128;
const ULTRA_HIGH_SPEED_COMPANION_PROBE_INTERVAL_MS = 2000;
/** Mid-speed timer path: avoid arming rAF/MessageChannel probes every late tick. */
const MID_SPEED_DEFERRED_STALL_MIN_MISS_MS = 24;
const MID_SPEED_DEFERRED_STALL_INTERVAL_MS = 1000;
const MID_SPEED_COMPANION_PROBE_MIN_MISS_MS = 48;
const MID_SPEED_COMPANION_PROBE_INTERVAL_MS = 2000;

function clamp(value: number, min: number, max: number) {
  return Math.max(min, Math.min(max, value));
}

export function resolvePlaybackClockDeltaMs(params: {
  rawDeltaMs: number;
  maxTickDeltaMs?: number | null;
  speed?: number;
}): number {
  const rawDeltaMs = Number.isFinite(params.rawDeltaMs)
    ? Math.max(0, params.rawDeltaMs)
    : 0;
  if (params.maxTickDeltaMs === null) {
    return rawDeltaMs;
  }
  if (
    typeof params.maxTickDeltaMs === "number" &&
    Number.isFinite(params.maxTickDeltaMs) &&
    params.maxTickDeltaMs > 0
  ) {
    return Math.min(rawDeltaMs, params.maxTickDeltaMs);
  }

  const absoluteSpeed =
    typeof params.speed === "number" && Number.isFinite(params.speed)
      ? Math.abs(params.speed)
      : 1;
  // ≥150x under dual-video+k6 regularly stalls 1–2s. A 750ms cap permanently
  // loses media time (~30–55x observed). Allow up to 2s catch-up per tick, but
  // do not fully uncap — multi-second leaps freeze paint/blit and can stall
  // the scheduler if onTick throws mid catch-up.
  // ≥30x also sees 300–500ms dual-video LOAFs; a 250ms cap drops observed
  // speed below the 2% soak tolerance (seen as ~28.8x for a 30x target).
  const defaultMaxTickDeltaMs =
    absoluteSpeed >= 150
      ? 2000
      : absoluteSpeed >= 30
        ? 1000
        : absoluteSpeed >= 8
          ? 500
          : 250;

  return Math.min(rawDeltaMs, defaultMaxTickDeltaMs);
}

interface PlaybackClockState {
  time: number;
  generation: number;
}

export default function usePlaybackClock({
  isPlaying,
  speed,
  minTime = 0,
  maxTime = Number.POSITIVE_INFINITY,
  initialTime = 0,
  uiFps = 10,
  maxTickDeltaMs,
  timeScaleSeconds = 10,
  onTick,
  onUiPublishRequested,
  onSchedulerSample,
  onTimingSample,
  onEnd,
}: UsePlaybackClockOptions) {
  const playheadRef = useRef<number>(initialTime);
  const rafRef = useRef<number | null>(null);
  const onTickRafRef = useRef<number | null>(null);
  const pendingOnTickRef = useRef<{
    time: number;
    deltaMs: number;
    generation: number;
  } | null>(null);
  const timerRef = useRef<number | null>(null);
  const speedRef = useRef<number>(speed);
  const boundsRef = useRef({ minTime, maxTime });
  const onTickRef = useRef<typeof onTick>(onTick);
  const onUiPublishRequestedRef = useRef<typeof onUiPublishRequested>(
    onUiPublishRequested,
  );
  const onSchedulerSampleRef = useRef<typeof onSchedulerSample>(
    onSchedulerSample,
  );
  const onTimingSampleRef = useRef<typeof onTimingSample>(onTimingSample);
  const onEndRef = useRef<typeof onEnd>(onEnd);
  const deferredUiPublishGenerationRef = useRef(0);
  const schedulerGenerationRef = useRef(0);

  const [currentTimeState, setCurrentTimeState] = useState<PlaybackClockState>({
    time: initialTime,
    generation: 0,
  });

  useLayoutEffect(() => {
    speedRef.current = speed;
  }, [speed]);

  useEffect(() => {
    boundsRef.current = { minTime, maxTime };
  }, [minTime, maxTime]);

  useEffect(() => {
    onTickRef.current = onTick;
  }, [onTick]);

  useEffect(() => {
    onUiPublishRequestedRef.current = onUiPublishRequested;
  }, [onUiPublishRequested]);

  useEffect(() => {
    onSchedulerSampleRef.current = onSchedulerSample;
  }, [onSchedulerSample]);

  useEffect(() => {
    onTimingSampleRef.current = onTimingSample;
  }, [onTimingSample]);

  useEffect(() => {
    onEndRef.current = onEnd;
  }, [onEnd]);

  const cancelScheduledTick = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (onTickRafRef.current !== null) {
      cancelAnimationFrame(onTickRafRef.current);
      onTickRafRef.current = null;
    }
    pendingOnTickRef.current = null;
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const invalidateDeferredUiPublishes = useCallback(() => {
    deferredUiPublishGenerationRef.current += 1;
  }, []);

  const invalidateSchedulerGeneration = useCallback(() => {
    schedulerGenerationRef.current += 1;
    invalidateDeferredUiPublishes();
  }, [invalidateDeferredUiPublishes]);

  const commitCurrentTime = useCallback(
    (
      nextTime: number,
      generation: number,
      options?: { preserveForwardMonotonicity?: boolean },
    ) => {
      const { minTime: minBound, maxTime: maxBound } = boundsRef.current;
      const clamped = clamp(nextTime, minBound, maxBound);

      setCurrentTimeState((previous) => {
        if (generation < previous.generation) {
          return previous;
        }

        if (
          generation === previous.generation &&
          Math.abs(previous.time - clamped) < 1e-9
        ) {
          return previous;
        }

        if (
          options?.preserveForwardMonotonicity &&
          generation === previous.generation &&
          clamped < previous.time &&
          speedRef.current >= 0
        ) {
          return previous;
        }

        return {
          time: clamped,
          generation,
        };
      });

      return clamped;
    },
    [],
  );

  const setTime = useCallback((nextTime: number) => {
    const { minTime: minBound, maxTime: maxBound } = boundsRef.current;
    const clamped = clamp(nextTime, minBound, maxBound);
    playheadRef.current = clamped;
    commitCurrentTime(clamped, schedulerGenerationRef.current);
    return clamped;
  }, [commitCurrentTime]);

  const setPlayheadSilently = useCallback((nextTime: number) => {
    const { minTime: minBound, maxTime: maxBound } = boundsRef.current;
    const clamped = clamp(nextTime, minBound, maxBound);
    playheadRef.current = clamped;
    return clamped;
  }, []);

  const freeze = useCallback(() => {
    invalidateSchedulerGeneration();
    cancelScheduledTick();
    commitCurrentTime(playheadRef.current, schedulerGenerationRef.current);
    return playheadRef.current;
  }, [cancelScheduledTick, commitCurrentTime, invalidateSchedulerGeneration]);

  useEffect(() => {
    if (!isPlaying) {
      invalidateSchedulerGeneration();
      cancelScheduledTick();
      commitCurrentTime(playheadRef.current, schedulerGenerationRef.current);
      return;
    }

    const playbackGeneration = schedulerGenerationRef.current + 1;
    schedulerGenerationRef.current = playbackGeneration;

    const absoluteSpeed = Math.abs(speed);
    const useTimerScheduler = absoluteSpeed >= 8;
    const isUltraHighSpeedTimerScheduler = absoluteSpeed >= 200;
    const resolvedSchedulerIntervalMs = resolveEEGViewerPlaybackSchedulerIntervalMs(
      absoluteSpeed,
      timeScaleSeconds,
    );
    const schedulerIntervalMs = useTimerScheduler
      ? resolvedSchedulerIntervalMs > 0
        ? resolvedSchedulerIntervalMs
        : 33
      : 0;

    // If playhead is already at or past the end, wrap to the beginning so
    // pressing Play at the last frame re-starts from t=0 instead of firing
    // onEnd on the very first tick with zero visible animation.
    {
      const { minTime: minBound, maxTime: maxBound } = boundsRef.current;
      if (
        speedRef.current >= 0 &&
        Number.isFinite(maxBound) &&
        playheadRef.current >= maxBound - 0.1
      ) {
        playheadRef.current = minBound;
        commitCurrentTime(minBound, playbackGeneration);
      }
    }

    let lastTs = performance.now();
    let lastUiPublishTs = lastTs;
    const uiPublishIntervalMs = Math.max(16, 1000 / Math.max(1, uiFps));
    let nextTimerTargetMs = lastTs + schedulerIntervalMs;
    let lastTickStartedAtMs: number | null = null;
    let lastTickCompletedAtMs: number | null = null;
    let lastTickWorkMs: number | null = null;
    let lastSchedulerSampleAtMs = Number.NEGATIVE_INFINITY;
    let lastCompanionProbeAtMs = Number.NEGATIVE_INFINITY;
    let clockAnchor = createPlaybackClockAnchor({
      wallMs: lastTs,
      playheadSeconds: playheadRef.current,
      speed: speedRef.current,
    });
    let lastTimingSampleAtMs = Number.NEGATIVE_INFINITY;
    const frameBudgetMs = resolvePlaybackFrameBudgetMs({
      scheduler: useTimerScheduler ? "timer" : "raf",
      schedulerIntervalMs,
    });
    // Sample drift/budget at ~2 Hz so we do not flood telemetry.
    const timingSampleIntervalMs = 500;

    const advanceTimerTarget = (pivotMs: number) => {
      if (!useTimerScheduler) {
        return;
      }

      while (nextTimerTargetMs <= pivotMs) {
        nextTimerTargetMs += schedulerIntervalMs;
      }
    };

    const scheduleNextTick = () => {
      if (schedulerGenerationRef.current !== playbackGeneration) {
        return;
      }

      if (useTimerScheduler) {
        const scheduledAtMs = performance.now();
        const targetAtMs = nextTimerTargetMs;
        const scheduledLateMs = Math.max(0, scheduledAtMs - targetAtMs);
        const delayMs = Math.max(0, targetAtMs - scheduledAtMs);
        timerRef.current = window.setTimeout(() => {
          timerRef.current = null;
          if (schedulerGenerationRef.current !== playbackGeneration) {
            return;
          }
          const firedAtMs = performance.now();
          const schedulerSampleCallback = onSchedulerSampleRef.current;
          const callbackLateMs = Math.max(
            0,
            firedAtMs - Math.max(targetAtMs, scheduledAtMs),
          );
          const overrunMs = Math.max(0, firedAtMs - targetAtMs);
          const maxMissMs = Math.max(
            overrunMs,
            scheduledLateMs,
            callbackLateMs,
          );
          const deferredStallMinMissMs = isUltraHighSpeedTimerScheduler
            ? ULTRA_HIGH_SPEED_DEFERRED_STALL_MIN_MISS_MS
            : MID_SPEED_DEFERRED_STALL_MIN_MISS_MS;
          const deferredStallIntervalMs = isUltraHighSpeedTimerScheduler
            ? ULTRA_HIGH_SPEED_DEFERRED_STALL_INTERVAL_MS
            : MID_SPEED_DEFERRED_STALL_INTERVAL_MS;
          const companionProbeMinMissMs = isUltraHighSpeedTimerScheduler
            ? ULTRA_HIGH_SPEED_COMPANION_PROBE_MIN_MISS_MS
            : MID_SPEED_COMPANION_PROBE_MIN_MISS_MS;
          const companionProbeIntervalMs = isUltraHighSpeedTimerScheduler
            ? ULTRA_HIGH_SPEED_COMPANION_PROBE_INTERVAL_MS
            : MID_SPEED_COMPANION_PROBE_INTERVAL_MS;

          // Always tick first. Mid-speed used to emit stall samples + triple
          // companion probes on every ≥8ms miss, which created a main-thread
          // death spiral (thousands of probes) and starved tile handoff / rAF.
          tick(firedAtMs);

          const callbackCompletedAtMs = performance.now();
          const shouldQueueDeferredStallSample =
            typeof schedulerSampleCallback === "function" &&
            maxMissMs >= deferredStallMinMissMs &&
            firedAtMs - lastSchedulerSampleAtMs >= deferredStallIntervalMs;
          const shouldQueueDeferredCompanionProbe =
            typeof schedulerSampleCallback === "function" &&
            maxMissMs >= companionProbeMinMissMs &&
            firedAtMs - lastCompanionProbeAtMs >= companionProbeIntervalMs;

          if (
            !shouldQueueDeferredStallSample &&
            !shouldQueueDeferredCompanionProbe
          ) {
            return;
          }

          if (shouldQueueDeferredStallSample) {
            lastSchedulerSampleAtMs = firedAtMs;
          }
          if (shouldQueueDeferredCompanionProbe) {
            lastCompanionProbeAtMs = firedAtMs;
          }

          window.setTimeout(() => {
            const missPhase =
              scheduledLateMs > 1 && callbackLateMs > 1
                ? "mixed"
                : scheduledLateMs > 1
                  ? "after-previous-work"
                  : callbackLateMs > 1
                    ? "before-callback"
                    : "none";
            const playheadTimeSeconds = playheadRef.current;
            const buildDeferredBaseSample = (): PlaybackClockSchedulerSample => ({
              scheduler: "timer",
              sampleKind: "deadline-miss",
              intervalMs: schedulerIntervalMs,
              scheduledAtMs,
              targetAtMs,
              firedAtMs,
              overrunMs,
              scheduledLateMs,
              callbackLateMs,
              missPhase,
              playheadTimeSeconds,
              previousTickStartedAtMs: lastTickStartedAtMs,
              previousTickCompletedAtMs: lastTickCompletedAtMs,
              previousTickWorkMs: lastTickWorkMs,
              callbackCompletedAtMs,
              rafDelayMs: null,
              messageChannelDelayMs: null,
              zeroDelayTimerMs: null,
            });

            if (shouldQueueDeferredStallSample) {
              schedulerSampleCallback?.(buildDeferredBaseSample());
            }

            if (!shouldQueueDeferredCompanionProbe) {
              return;
            }

            let rafDelayMs: number | null = null;
            let messageChannelDelayMs: number | null = null;
            let zeroDelayTimerMs: number | null = null;
            let rafProbeResolved =
              typeof requestAnimationFrame === "undefined";
            let messageProbeResolved = typeof MessageChannel === "undefined";
            let timerProbeResolved = false;

            const flushCompanionProbe = () => {
              if (
                !rafProbeResolved ||
                !messageProbeResolved ||
                !timerProbeResolved
              ) {
                return;
              }

              schedulerSampleCallback?.({
                ...buildDeferredBaseSample(),
                sampleKind: "companion-probe",
                callbackCompletedAtMs,
                rafDelayMs,
                messageChannelDelayMs,
                zeroDelayTimerMs,
              });
            };

            if (typeof requestAnimationFrame !== "undefined") {
              requestAnimationFrame(() => {
                rafDelayMs = Math.max(
                  0,
                  performance.now() - callbackCompletedAtMs,
                );
                rafProbeResolved = true;
                flushCompanionProbe();
              });
            }

            if (typeof MessageChannel !== "undefined") {
              const channel = new MessageChannel();
              channel.port1.onmessage = () => {
                messageChannelDelayMs = Math.max(
                  0,
                  performance.now() - callbackCompletedAtMs,
                );
                messageProbeResolved = true;
                channel.port1.close();
                channel.port2.close();
                flushCompanionProbe();
              };
              channel.port2.postMessage(null);
            }

            window.setTimeout(() => {
              zeroDelayTimerMs = Math.max(
                0,
                performance.now() - callbackCompletedAtMs,
              );
              timerProbeResolved = true;
              flushCompanionProbe();
            }, 0);
          }, 0);
          return;
        }, delayMs);
        return;
      }

      if (schedulerGenerationRef.current !== playbackGeneration) {
        return;
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    const tick = (ts: number) => {
      if (schedulerGenerationRef.current !== playbackGeneration) {
        return;
      }

      const tickStartedAtMs = performance.now();
      const rawDeltaMs = Math.max(0, ts - lastTs);
      const deltaMs = resolvePlaybackClockDeltaMs({
        rawDeltaMs,
        maxTickDeltaMs,
        speed: speedRef.current,
      });
      lastTs = ts;

      const { minTime: minBound, maxTime: maxBound } = boundsRef.current;
      const nextRaw = playheadRef.current + (deltaMs / 1000) * speedRef.current;
      const nextClamped = clamp(nextRaw, minBound, maxBound);
      playheadRef.current = nextClamped;
      if (typeof window !== "undefined" && speedRef.current < 150) {
        (window as any).__eegSoakPlaybackTimeSeconds = nextClamped;
      }

      // Keep viewer work (buffer flush / paint schedule) off this callback so
      // LoAF scripts attributed to usePlaybackClock stay playhead-only.
      pendingOnTickRef.current = {
        time: nextClamped,
        deltaMs,
        generation: playbackGeneration,
      };
      if (onTickRafRef.current === null) {
        const flushDeferredOnTick = () => {
          onTickRafRef.current = null;
          const job = pendingOnTickRef.current;
          pendingOnTickRef.current = null;
          if (!job || schedulerGenerationRef.current !== job.generation) {
            return;
          }
          try {
            onTickRef.current?.(job.time, job.deltaMs);
          } catch {
            // Keep the scheduler alive even if a paint/tick handler throws after a
            // large catch-up step (observed as a hard 0x stall at ≥150x soak).
          }
        };
        if (typeof requestAnimationFrame === "function") {
          onTickRafRef.current = requestAnimationFrame(flushDeferredOnTick);
        } else {
          flushDeferredOnTick();
        }
      }
      if (schedulerGenerationRef.current !== playbackGeneration) {
        return;
      }

      const effectivePlayhead = clamp(playheadRef.current, minBound, maxBound);
      playheadRef.current = effectivePlayhead;

      if (ts - lastUiPublishTs >= uiPublishIntervalMs) {
        const deferredUiPublishGeneration =
          deferredUiPublishGenerationRef.current;
        onUiPublishRequestedRef.current?.({
          requestedAtMs: performance.now(),
          playheadTimeSeconds: effectivePlayhead,
          deltaMs,
          uiPublishIntervalMs,
        });
        // Mark as a non-urgent transition so React defers the EEGViewer re-render
        // to idle time — prevents the 17k-line component from blocking rAF callbacks.
        startTransition(() => {
          if (deferredUiPublishGenerationRef.current !== deferredUiPublishGeneration) {
            return;
          }

          commitCurrentTime(effectivePlayhead, playbackGeneration, {
            preserveForwardMonotonicity: speedRef.current >= 0,
          });
        });
        lastUiPublishTs = ts;
      }

      const reachedEnd =
        (speedRef.current >= 0 && effectivePlayhead >= maxBound) ||
        (speedRef.current < 0 && effectivePlayhead <= minBound);

      const tickCompletedAtMs = performance.now();
      lastTickStartedAtMs = tickStartedAtMs;
      lastTickCompletedAtMs = tickCompletedAtMs;
      lastTickWorkMs = tickCompletedAtMs - tickStartedAtMs;

      if (
        typeof onTimingSampleRef.current === "function" &&
        tickCompletedAtMs - lastTimingSampleAtMs >= timingSampleIntervalMs
      ) {
        // Re-anchor when speed changes so drift reflects the current model.
        if (Math.abs(clockAnchor.speed - speedRef.current) > 1e-6) {
          clockAnchor = createPlaybackClockAnchor({
            wallMs: tickCompletedAtMs,
            playheadSeconds: effectivePlayhead,
            speed: speedRef.current,
          });
        }
        const drift = measurePlaybackClockDrift({
          anchor: clockAnchor,
          nowMs: tickCompletedAtMs,
          playheadSeconds: effectivePlayhead,
          speed: speedRef.current,
        });
        const timingSample = {
          scheduler: (useTimerScheduler ? "timer" : "raf") as "timer" | "raf",
          playheadTimeSeconds: effectivePlayhead,
          deltaMs,
          tickWorkMs: lastTickWorkMs,
          frameBudgetMs,
          frameBudgetRemainingMs: resolveFrameBudgetRemainingMs({
            tickWorkMs: lastTickWorkMs,
            frameBudgetMs,
          }),
          drift,
          speed: speedRef.current,
        };
        // Publish diagnostics off the animation-frame hot path.
        const timingCallback = onTimingSampleRef.current;
        window.setTimeout(() => {
          timingCallback?.(timingSample);
        }, 0);
        lastTimingSampleAtMs = tickCompletedAtMs;
      }

      if (schedulerGenerationRef.current !== playbackGeneration) {
        return;
      }

      if (reachedEnd) {
        const soakLoopAtEnd =
          typeof window !== "undefined" &&
          (window as any).__eegSoakWrapAtRecordingEnd === true;
        // Soak-only: wrap to t=0 so finite recordings can keep scoring blit/timing.
        if (
          soakLoopAtEnd &&
          speedRef.current >= 0 &&
          Number.isFinite(maxBound) &&
          maxBound > minBound
        ) {
          playheadRef.current = minBound;
          clockAnchor = createPlaybackClockAnchor({
            wallMs: ts,
            playheadSeconds: minBound,
            speed: speedRef.current,
          });
          pendingOnTickRef.current = {
            time: minBound,
            deltaMs,
            generation: playbackGeneration,
          };
          commitCurrentTime(minBound, playbackGeneration);
          advanceTimerTarget(ts);
          scheduleNextTick();
          return;
        }
        commitCurrentTime(effectivePlayhead, playbackGeneration);
        onEndRef.current?.(effectivePlayhead);
        rafRef.current = null;
        timerRef.current = null;
        return;
      }

      advanceTimerTarget(ts);
      scheduleNextTick();
    };

    scheduleNextTick();
    return () => {
      cancelScheduledTick();
    };
  }, [
    cancelScheduledTick,
    commitCurrentTime,
    invalidateSchedulerGeneration,
    isPlaying,
    maxTickDeltaMs,
    speed,
    timeScaleSeconds,
    uiFps,
  ]);

  return {
    currentTime: currentTimeState.time,
    setCurrentTime: setTime,
    setPlayheadSilently,
    playheadRef,
    freeze,
  };
}
